// <product-switcher> — shared base for PDP inline "switcher" custom elements.
//
// A switcher offers a button per sibling product along one axis (fabric, size,
// …). Siblings are modelled as separate products linked by shared metafields, so
// switching swaps the product section in place (via window.swapProductSection,
// shared with the colour swatch links) rather than navigating — preserving
// scroll position.
//
// This base owns everything the switchers have in common: the Storefront fetch
// (cached), the once-per-fabric/size render, the button markup, prefetch
// warming, and hiding itself when there's nothing to switch to. It renders in
// one of two layouts, chosen by data-layout: the default inline row of buttons,
// or "dropdown" — a trigger plus a <slide-out-modal> panel holding the same
// buttons in a two-column grid, for the sticky ATC. Each concrete switcher is a
// thin subclass that configures only what differs:
//
//   setup()                 read data-* attributes; return false if unusable
//   get cacheKey            de-dupe the Storefront lookup across re-inits
//   get currentKey          the option key the current product represents
//   get currentLabel        fallback button/label text for the current key
//   get labelText           the axis label ("Fibre", "Size")
//   get orderList           preferred key order; unknown keys fall to the end
//   buildQuery()            { query, variables } for the Storefront API
//   parseNodes(nodes)       Storefront nodes -> Map<key, { label, record, … }>
//   resolveRecord(k, entry) link target for a non-current key (default: record)
//   currentFallbackEntry()  entry to use if the query didn't return the current
//   onInit()                optional side effect before first render
//
// See PillowcaseSizeSelector below. (A fabric switcher subclass lived here too;
// it's been removed for now but the base is kept generic so it can return.)

const STOREFRONT_API_VERSION = '2023-04';

// Storefront lookups are cached across re-inits (each section swap re-creates
// these elements). One Map is shared by every switcher, so keys are namespaced
// by subclass (e.g. "fabric:…", "size:…").
const fetchCache = new Map();

class ProductSwitcher extends HTMLElement {
	connectedCallback() {
		if (this._initialised) return;
		this._initialised = true;

		this.classList.add('product-switcher');
		this.sectionId = document.getElementById('ProductSection-product-template')?.dataset.sectionId;

		// The element ships with a server-rendered skeleton (see the snippet) so a
		// placeholder is painted on initial page load, before this deferred module
		// runs. render() replaces it with the real buttons, or collapse() clears it.
		if (!this.setup()) return this.collapse();

		this.onInit();
		this.render();
	}

	// --- hooks, overridden by subclasses ---------------------------------------
	setup() { return true; }
	onInit() {}
	get cacheKey() { return this.tagName; }
	get currentKey() { return ''; }
	get currentLabel() { return ''; }
	get labelText() { return ''; }
	get orderList() { return []; }
	buildQuery() { return { query: '', variables: {} }; }
	parseNodes() { return new Map(); }
	resolveRecord(key, entry) { return entry.record; }
	currentFallbackEntry() { return { label: this.currentLabel }; }

	// --- shared plumbing -------------------------------------------------------
	fetchEntries() {
		if (fetchCache.has(this.cacheKey)) return fetchCache.get(this.cacheKey);

		const { query, variables } = this.buildQuery();

		const promise = fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': theme.storefrontToken,
			},
			body: JSON.stringify({ query, variables }),
		})
			.then(resp => {
				if (!resp.ok) throw new Error(`${this.tagName} lookup failed: ${resp.status}`);
				return resp.json();
			})
			.then(json => this.parseNodes(json?.data?.products?.nodes || []));

		fetchCache.set(this.cacheKey, promise);
		return promise;
	}

	// Builds the axis label ("Fibre: Linen"). Visually hidden — see CSS.
	makeLabel(currentText) {
		const labelEl = document.createElement('span');
		labelEl.className = 'product-switcher__label';
		labelEl.append(`${this.labelText}: `);
		const current = document.createElement('span');
		current.className = 'product-switcher__current';
		current.textContent = currentText;
		labelEl.appendChild(current);
		return labelEl;
	}

	// Removes the switcher (and any skeleton) from the page.
	collapse() {
		this.replaceChildren();
		this.hidden = true;
	}

	async render() {
		let entries;
		try {
			entries = await this.fetchEntries();
		} catch (e) {
			return this.collapse(); // fail silently — the PDP is usable without the switcher
		}

		// Ensure the current option is represented even if the query missed it.
		if (!entries.has(this.currentKey)) {
			entries.set(this.currentKey, this.currentFallbackEntry());
		}

		// Only show the switcher when there's actually another option to pick.
		if (entries.size < 2) return this.collapse();

		const order = this.orderList;
		const ordered = [...entries.entries()].sort((a, b) => {
			const ia = order.indexOf(a[0]);
			const ib = order.indexOf(b[0]);
			return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
		});

		const prefetchUrls = [];
		const list = this.buildButtons(ordered, prefetchUrls);
		const currentLabel = entries.get(this.currentKey)?.label || this.currentLabel;

		if (this.dataset.layout == 'dropdown') {
			if (!this.fillDropdown(list, currentLabel)) return this.collapse();
		} else {
			this.replaceChildren(this.makeLabel(currentLabel), list);
		}

		this.hidden = false;

		// Warm the target sections while the browser is idle so the first switch
		// is instant, even on touch devices where there's no hover to prefetch on.
		if (window.prefetchProductSection && this.sectionId && prefetchUrls.length) {
			const warm = () => prefetchUrls.forEach(url => window.prefetchProductSection(url, this.sectionId));
			if (window.requestIdleCallback) requestIdleCallback(warm, { timeout: 3000 });
			else setTimeout(warm, 1500);
		}
	}

	// Builds the <ul> of options: a link per key that switches product, and a
	// plain (inert) button for the one already showing. Collects the link targets
	// into prefetchUrls so the caller can warm them.
	buildButtons(ordered, prefetchUrls) {
		const list = document.createElement('ul');
		list.className = 'product-switcher__buttons';

		for (const [key, entry] of ordered) {
			const item = document.createElement('li');
			item.className = 'product-switcher__button';

			if (key === this.currentKey) {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.setAttribute('aria-current', 'true');
				btn.setAttribute('data-active', '');
				btn.textContent = entry.label;
				item.appendChild(btn);
				list.appendChild(item);
				continue;
			}

			const record = this.resolveRecord(key, entry);
			if (!record || !record.u) continue;

			prefetchUrls.push(record.u);

			const link = document.createElement('a');
			link.href = record.u;
			link.textContent = entry.label;

			const prefetch = () => window.prefetchProductSection && window.prefetchProductSection(record.u, this.sectionId);
			link.addEventListener('mouseenter', prefetch);
			link.addEventListener('mousedown', prefetch);
			link.addEventListener('touchstart', prefetch, { passive: true });

			link.addEventListener('click', e => {
				if (!window.swapProductSection || !this.sectionId) return; // let the browser navigate
				e.preventDefault();
				window.swapProductSection({
					link: record.u,
					sectionId: this.sectionId,
					title: record.t,
					image: record.i,
					productId: record.p,
				});
			});

			item.appendChild(link);
			list.appendChild(item);
		}

		return list;
	}

	// Dropdown layout: the trigger and an empty <slide-out-modal> are authored in
	// the snippet (the modal reads its config from attributes as it's constructed,
	// so it can't be built here), leaving this to drop the buttons into the panel
	// and wire the trigger up. Returns false if the shell isn't there.
	fillDropdown(list, currentLabel) {
		const trigger = this.querySelector('.product-switcher__trigger');
		const modal = this.querySelector('.product-switcher__modal');
		const slot = modal && modal.querySelector('.product-switcher__buttons');
		if (!trigger || !slot) return false;

		list.classList.add('product-switcher__buttons--grid');
		slot.replaceWith(list);

		const label = trigger.querySelector('.product-switcher__trigger-label');
		if (label) label.textContent = currentLabel;

		trigger.addEventListener('click', () => modal.toggle());
		modal.addEventListener('modalopen', () => trigger.setAttribute('aria-expanded', 'true'));
		// Flip the arrow the moment closing starts, rather than after the modal's
		// 300ms close animation, matching the sticky bar's colour dropdown.
		modal.addEventListener('modalstartclose', () => trigger.setAttribute('aria-expanded', 'false'));

		// Close before the link's own handler runs: as a mobile sheet the modal
		// locks page scroll via <html class="modal-open">, and switching size
		// replaces the section this element lives in, so nothing would be left to
		// unlock it. Capture phase, since the swap listener is on the link itself.
		list.addEventListener('click', () => modal.close('Option'), true);

		return true;
	}
}

function toRecord(node) {
	if (!node || !node.handle) return null;
	return {
		u: `/products/${node.handle}`,
		t: node.title || '',
		i: node.featuredImage?.url || '',
		p: node.id ? String(node.id).split('/').pop() : '',
	};
}

// <pillowcase-size-selector> — switch between pillowcase sizes of the same
// fabric and colour.
//
// Pillowcase size is modelled as separate products with distinct product types
// ("Standard Pillowcase", "European Pillowcase", "King Pillowcase", plus the
// scalloped equivalents), linked by a shared fabric + colour and carrying a
// "Size" option. Matching on fabric + colour preserves the colour on switch and
// keeps the scalloped and plain lines separate automatically (their colour names
// differ, e.g. "Terracotta" vs "Terracotta & Rust Scalloped").

// Every pillowcase product type we switch between.
const PILLOWCASE_TYPES = [
	'Standard Pillowcase',
	'European Pillowcase',
	'King Pillowcase',
	'Scalloped Pillowcase',
	'Scalloped European Pillowcase',
];

// Display order; the size comes from each product's "Size" option value.
const SIZE_ORDER = ['Standard Size', 'European Size', 'King Size'];

const PILLOWCASE_QUERY = /* GraphQL */ `
	query pillowcaseSizeSwitcher($q: String!) {
		products(first: 250, query: $q) {
			nodes {
				handle
				title
				id
				featuredImage { url }
				fabric: metafield(namespace: "custom", key: "fabric") {
					reference { ... on Metaobject { handle } }
				}
				colour: metafield(namespace: "custom", key: "colour") { value }
				options { name values }
			}
		}
	}`;

// "Standard Size" -> "Standard"; leaves anything without a trailing "Size" as-is.
function sizeLabel(value) {
	return value.replace(/\s*Size$/i, '').trim() || value;
}

class PillowcaseSizeSelector extends ProductSwitcher {
	setup() {
		this.fabric = this.dataset.fabric;
		this.colour = (this.dataset.colour || '').trim();
		this.currentSize = this.dataset.currentSize;
		return !!(this.fabric && this.colour && this.currentSize);
	}

	get cacheKey() { return `size:${this.fabric}::${this.colour}`; }
	get currentKey() { return this.currentSize; }
	get currentLabel() { return sizeLabel(this.currentSize); }
	get labelText() { return 'Size'; }
	get orderList() { return SIZE_ORDER; }

	currentFallbackEntry() {
		return {
			label: sizeLabel(this.currentSize),
			record: {
				u: this.dataset.currentUrl,
				t: this.dataset.currentTitle,
				i: this.dataset.currentImage,
				p: this.dataset.currentProductId,
			},
		};
	}

	buildQuery() {
		const q = PILLOWCASE_TYPES.map(t => `product_type:'${t.replace(/'/g, "\\'")}'`).join(' OR ');
		return { query: PILLOWCASE_QUERY, variables: { q } };
	}

	parseNodes(nodes) {
		const sizes = new Map(); // size value -> { label, record }

		for (const node of nodes) {
			if (node.fabric?.reference?.handle !== this.fabric) continue;
			if ((node.colour?.value || '').trim() !== this.colour) continue;

			const size = node.options?.find(o => o.name === 'Size')?.values?.[0];
			if (!size || sizes.has(size)) continue;

			sizes.set(size, { label: sizeLabel(size), record: toRecord(node) });
		}

		return sizes;
	}
}


if (!customElements.get('pillowcase-size-selector')) {
	customElements.define('pillowcase-size-selector', PillowcaseSizeSelector);
}
